/**
 * Product Form Component - Handles product purchasing and variant selection
 * Manages form submission, cart updates, and variant-specific UI changes
 */

import { Component } from "./component-base.js";
import { updateCartBubble } from "./utilities.js";
import { AddToCartComponent } from "./add-to-cart.js";
import { VariantSelectorComponent } from "./variant-selector.js";
import { QuantitySelectorComponent } from "./quantity-selector.js";
import { ProductMediaComponent } from "./product-media.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

/**
 * Initializes the product form component and its dependencies
 * Should be called on product template pages
 */
export async function initProductForm() {

  /**
   * Product form component that handles variant selection and cart operations
   * @extends Component
   */
  class ProductFormComponent extends Component {
    requiredRefs = [
      "variantId",
      "liveRegion",
      "errorMessage",
      "price",
      "comparePrice",
      "addToCartButton",
      "addToCartButtonContainer"
    ];

    connectedCallback() {
      super.connectedCallback();
      this.validateRefs();

      const quantityInput = this.querySelector(
        'quantity-selector-component input[name="quantity"]'
      );
      if (quantityInput) this.refs.quantityInput = quantityInput;

      // Attach to the actual form to ensure preventDefault always runs
      this.formEl = this.querySelector('form');
      if (this.formEl) this.formEl.addEventListener('submit', this.#handleSubmit, { capture: true });
      // Removed bubbling fallback to avoid double submission
      // Bus-based variant handling only
      ThemeBus.addEventListener(EVENTS.variantChange, this.#handleVariantChangeFromBus);
      
      // Handle initial compare price visibility
      this.#updateComparePriceVisibility();

      // Ensure cart data is loaded
      this.#ensureCartData();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.formEl) this.formEl.removeEventListener('submit', this.#handleSubmit);
      // Bus-based variant handling only
      ThemeBus.removeEventListener(EVENTS.variantChange, this.#handleVariantChangeFromBus);
    }

    /**
     * Updates compare price visibility based on current state
     */
    #updateComparePriceVisibility() {
      if (!this.refs.comparePrice) return;
      
      const comparePriceText = this.refs.comparePrice.textContent.trim();
      
      // If compare price is empty, hide it
      // The Liquid template only renders content if compare_at_price exists and should be shown
      if (!comparePriceText) {
        this.refs.comparePrice.classList.add('hidden');
      } else {
        this.refs.comparePrice.classList.remove('hidden');
      }
    }

    /**
     * Updates the cart data from Shopify's cart.js endpoint
     */
    async #updateCartData() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Failed to fetch cart');
        
        const cartData = await response.json();
        window.__cart = cartData;
        updateCartBubble();
      } catch (error) {
        console.error('[ProductForm] Error fetching cart:', error);
      }
    }

    /**
     * Ensures cart data is loaded on page load
     */
    async #ensureCartData() {
      if (!window.__cart || !window.__cart.items) {
        await this.#updateCartData();
      }
    }

    /**
     * Handles form submission and adds product to cart
     * @param {Event} event - The form submit event
     */
    #handleSubmit = async (event) => {
      event.preventDefault();

      const form = this.querySelector("form");
      if (!form) return;

      // Check if we need to block due to cart maximum
      const variantId = parseInt(this.refs.variantId.value);
      const requestedQuantity = parseInt(this.refs.quantityInput?.value || 1);

      if (!this.#canAddToCart(variantId, requestedQuantity)) {
        return; // Error message already shown by #canAddToCart
      }

      const formData = new FormData(form);
      formData.set("quantity", requestedQuantity);

      this.refs.addToCartButton.querySelector(".button-spinner")?.classList.remove("hidden");
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButtonContainer?.setLoading?.();

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        const result = await response.json();

        if (result.status && result.status !== 200) {
          this.#setLiveRegionText(result.message || "Error adding to cart");
          this.refs.addToCartButtonContainer?.enable?.();
          return;
        }

        this.refs.addToCartButtonContainer?.setSuccess?.();
        this.#setLiveRegionText("Added to cart");

        // Short delay to show success, then fetch cart and update
        setTimeout(async () => {
          // Fetch the full cart to get updated quantities
          await this.#updateCartData();

          // Small delay to ensure cart data is set
          setTimeout(() => {
            // Now dispatch events
            ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
            ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));

        if (this.refs.quantityInput) {
          this.refs.quantityInput.value = 1;
          this.refs.quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
          }, 100);
        }, 500);
      } catch (error) {
        this.#setLiveRegionText("Add to cart failed");
        this.refs.addToCartButtonContainer?.enable?.();
      }
    };

    /**
     * Checks if the variant can be added to cart based on inventory and cart quantity
     * @param {number} variantId - The variant ID
     * @param {number} requestedQuantity - The quantity to add
     * @returns {boolean} Whether the add to cart should proceed
     */
    #canAddToCart(variantId, requestedQuantity) {
      try {
        const productJson = document.getElementById('product-json');
        if (!productJson) return true; // If no product data, allow (backend will handle)

        const productData = JSON.parse(productJson.textContent);
        const variant = productData.variants.find(v => v.id === variantId);

        if (!variant) return true;

        // Check if inventory is managed and tracked
        if (variant.inventory_management !== 'shopify' || variant.inventory_policy !== 'deny') {
          return true; // Allow if not tracking or allowing backorders
        }

        const inventoryQuantity = variant.inventory_quantity || 0;
        const quantityInCart = this.#getQuantityInCart(variantId);
        const remainingQuantity = inventoryQuantity - quantityInCart;

        if (remainingQuantity <= 0) {
          // Show error message
          this.#setLiveRegionText("Maximum quantity already in cart");
          return false;
        }

        if (requestedQuantity > remainingQuantity) {
          // Show error message with available quantity
          this.#setLiveRegionText(`Only ${remainingQuantity} available (${quantityInCart} already in cart)`);
          return false;
        }

        return true;
      } catch (error) {
        console.error('[ProductForm] Error checking cart quantity:', error);
        return true; // Allow on error
      }
    }

    /**
     * Handles variant selection changes and updates UI accordingly
     * @param {CustomEvent} event - The variant change event
     */
    #handleVariantChange = (event) => {
      const variant = event.detail.variant;
      if (!variant) return;

      this.refs.variantId.value = variant.id;
      this.currentInventory = variant.inventory_quantity;

      const continueSelling = variant.inventory_policy === "continue";
      const inventoryManagement = variant.inventory_management;

      // Set inventory limit (not accounting for cart - we check at submission instead)
      let inventoryLimit;
      if (continueSelling || inventoryManagement !== 'shopify') {
        inventoryLimit = Infinity;
      } else {
        inventoryLimit = variant.inventory_quantity || 0;
      }

      if (!variant.available && !continueSelling) {
        this.refs.addToCartButtonContainer?.disable?.();
      } else {
        this.refs.addToCartButtonContainer?.enable?.();
      }

      if (this.refs.price) {
        this.refs.price.textContent = this.#formatMoney(variant.price);
      }

      if (this.refs.comparePrice) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          // Always set content first, then remove hidden class
          this.refs.comparePrice.textContent = this.#formatMoney(variant.compare_at_price);
          this.refs.comparePrice.classList.remove('hidden');
        } else {
          // Add hidden class and clear content
          this.refs.comparePrice.classList.add('hidden');
          this.refs.comparePrice.textContent = '';
        }
      }

      ThemeBus.dispatchEvent(new CustomEvent(EVENTS.variantInventory, { detail: { max: inventoryLimit, preorder: !!variant.preorder } }));

      ThemeBus.dispatchEvent(new CustomEvent(EVENTS.variantOutOfStock, { detail: { variant } }));

      // Update URL with selected variant
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    };

    #handleVariantChangeFromBus = (event) => {
      // Reuse same logic
      this.#handleVariantChange(event);
    };

    /**
     * Gets the quantity of a variant already in the cart
     * @param {number} variantId - The variant ID to check
     * @returns {number} The quantity in cart
     */
    #getQuantityInCart(variantId) {
      if (!window.__cart || !window.__cart.items) return 0;

      const cartItem = window.__cart.items.find(item => item.variant_id == variantId);
      return cartItem ? cartItem.quantity : 0;
    }

    /**
     * Updates the screen reader accessible live region and shows visual feedback for errors
     * @param {string} text - The text to announce
     */
    #setLiveRegionText(text) {
      // Update screen reader live region (always hidden)
      if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = text;
    }

      // Show error messages visually in the dedicated error div
      if (!this.refs.errorMessage) return;

      const isError = text.toLowerCase().includes('maximum') ||
                      text.toLowerCase().includes('only') ||
                      text.toLowerCase().includes('error') ||
                      text.toLowerCase().includes('failed');

      if (isError) {
        this.refs.errorMessage.textContent = text;
        this.refs.errorMessage.classList.remove('hidden');

        // Auto-hide after 5 seconds
        setTimeout(() => {
          this.refs.errorMessage.classList.add('hidden');
          this.refs.errorMessage.textContent = '';
        }, 5000);
      } else {
        // Hide error message for success messages
        this.refs.errorMessage.classList.add('hidden');
        this.refs.errorMessage.textContent = '';
      }
    }

    /**
     * Formats money using Shopify's currency settings
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price string
     */
    #formatMoney(cents) {
      const currency = window.shopCurrency || "GBP";
      const locale = window.shopLocale || "en-GB";

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(cents / 100);
    }
  }

  if (!customElements.get("product-form-component")) {
    customElements.define("product-form-component", ProductFormComponent);
  }
}

// Auto-initialize when module loads
initProductForm();
